import { useState, useCallback, useEffect, useRef } from "react";
import { useAuth } from "../context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Progress } from "../components/ui/progress";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../components/ui/tabs";
import { toast } from "sonner";
import { 
  Upload, 
  FileSpreadsheet, 
  FileText, 
  CheckCircle2, 
  XCircle, 
  Loader2,
  RefreshCcw,
  RefreshCw,
  Trash2,
  AlertTriangle,
  Download,
  Database,
  Calendar,
  TrendingUp,
  Network,
  History,
  Users,
  Bell,
} from "lucide-react";
import { Checkbox } from "../components/ui/checkbox";
import { useDropzone } from "react-dropzone";
import { Link } from "react-router-dom";
import { AccountMasterInner } from "./AccountCreationMaster";

const FILE_TYPES = {
  WBR9: {
    id: "wbr9",
    name: "WBR9 (CAMS)",
    description: "Investor Master & Balance Units",
    acceptedFiles: ".txt, .xlsx, .xls, .zip",
    icon: FileSpreadsheet,
    color: "bg-blue-100 text-blue-700",
    requiresPassword: true,
    defaultPassword: "Laksh@0208",
  },
  WBR9D: {
    id: "wbr9d",
    name: "WBR9D (CAMS)",
    description: "Investor Master (Historical)",
    acceptedFiles: ".txt, .zip",
    icon: FileText,
    color: "bg-cyan-100 text-cyan-700",
    requiresPassword: true,
    defaultPassword: "Laksh@0208",
  },
  MFSD211: {
    id: "mfsd211",
    name: "MFSD 211 (Karvy)",
    description: "Investor Master",
    acceptedFiles: ".txt, .zip, .xlsx, .xls",
    icon: FileSpreadsheet,
    color: "bg-green-100 text-green-700",
    requiresPassword: true,
    defaultPassword: "Laksh@0208",
  },
  MFSD203: {
    id: "mfsd203",
    name: "MFSD 203 (Karvy)",
    description: "Balance Units / AUM",
    acceptedFiles: ".txt, .zip, .xlsx, .xls, .csv",
    icon: FileSpreadsheet,
    color: "bg-purple-100 text-purple-700",
    requiresPassword: true,
    defaultPassword: "Laksh@0208",
  },
  WBR2: {
    id: "wbr2",
    name: "WBR2 (CAMS)",
    description: "Transaction Details",
    acceptedFiles: ".txt, .xlsx, .xls, .zip",
    icon: FileText,
    color: "bg-pink-100 text-pink-700",
    requiresPassword: true,
    defaultPassword: "Laksh@0208",
  },
  WBR2A: {
    id: "wbr2a",
    name: "WBR2A (CAMS)",
    description: "Yearly Transaction Details (ZIP/RAR with multiple files)",
    acceptedFiles: ".txt, .zip, .rar",
    icon: FileText,
    color: "bg-fuchsia-100 text-fuchsia-700",
    requiresPassword: true,
    defaultPassword: "Laksh@0208",
  },
  MFSD201: {
    id: "mfsd201",
    name: "MFSD 201 (Karvy)",
    description: "Transaction Details",
    acceptedFiles: ".txt, .zip, .dbf",
    icon: FileSpreadsheet,
    color: "bg-rose-100 text-rose-700",
    requiresPassword: true,
    defaultPassword: "Laksh@0208",
  },
  BSE_SCHEME: {
    id: "bse_scheme",
    name: "BSE Scheme Master",
    description: "ISIN & Scheme Details",
    acceptedFiles: ".txt",
    icon: FileText,
    color: "bg-orange-100 text-orange-700",
  },
  AMFI_MASTER: {
    id: "amfi_master",
    name: "AMFI Master",
    description: "Scheme Categories & Classification",
    acceptedFiles: ".xlsx, .xls, .csv",
    icon: FileSpreadsheet,
    color: "bg-teal-100 text-teal-700",
  },
  BSE_NAV: {
    id: "bse_nav",
    name: "BSE NAV (Historical)",
    description: "Download from bsestarmf.in/RptNavMaster.aspx",
    acceptedFiles: ".txt",
    icon: FileText,
    color: "bg-indigo-100 text-indigo-700",
  },
  PULSE_LABS: {
    id: "pulse_labs",
    name: "Scheme Master - Pulse Labs",
    description: "Scheme Code to Product Code mappings with asset classification",
    acceptedFiles: ".csv",
    icon: FileSpreadsheet,
    color: "bg-amber-100 text-amber-700",
  },
  COLUMN_MAPPING: {
    id: "column_mapping",
    name: "Investor Master Mapping Guide",
    description: "Column reference for populating investor_master from WBR9/MFSD211",
    acceptedFiles: ".xlsx, .xls",
    icon: FileSpreadsheet,
    color: "bg-amber-100 text-amber-700",
    hasDownload: true,
    downloadUrl: "/api/column-mapping/download",
    dbName: "column_mapping_guide",
  },
  FLAG_MAPPING: {
    id: "flag_mapping",
    name: "Transaction Flag Mapping",
    description: "Map CAMS/KARVY flag values to common standardized names",
    acceptedFiles: ".xlsx, .xls, .csv",
    icon: FileSpreadsheet,
    color: "bg-violet-100 text-violet-700",
    hasDownload: true,
    downloadUrl: "/api/flag-mapping/download-template",
    hasCurrentDownload: true,
    currentDownloadUrl: "/api/flag-mapping/download",
    dbName: "flag_mapping_config",
  },
  TXN_MASTER_TEMPLATE: {
    id: "txn_master_template",
    name: "Transaction Master Mapping Guide",
    description: "Column mapping template for building Transaction Master",
    acceptedFiles: ".xlsx, .xls",
    icon: FileSpreadsheet,
    color: "bg-emerald-100 text-emerald-700",
    hasDownload: true,
    downloadUrl: "/api/transaction-master/template/download",
    dbName: "txn_master_mapping_guide",
  },
  SUB_BROKER: {
    id: "sub_broker",
    name: "Sub Broker (Associate Master)",
    description: "100-column Associate Master bulk upload",
    acceptedFiles: ".xlsx, .csv",
    icon: Network,
    color: "bg-teal-100 text-teal-700",
  },
};

const FileUploadZone = ({ fileType, onUpload, isUploading }) => {
  const config = FILE_TYPES[fileType];
  const Icon = config.icon;

  const onDrop = useCallback((acceptedFiles) => {
    if (acceptedFiles.length > 0) {
      // For MFSD201, pass all files if multiple DBF files are uploaded
      if (fileType === "MFSD201" && acceptedFiles.length > 1) {
        onUpload(acceptedFiles, fileType, true); // true = multiple files
      } else {
        onUpload(acceptedFiles[0], fileType, false);
      }
    }
  }, [fileType, onUpload]);

  // Determine accepted file types based on file type
  const getAcceptedTypes = () => {
    if (fileType === "BSE_SCHEME" || fileType === "BSE_NAV") {
      return { "text/plain": [".txt"] };
    }
    if (fileType === "AMFI_MASTER") {
      return { 
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [".xlsx"], 
        "application/vnd.ms-excel": [".xls"],
        "text/csv": [".csv"]
      };
    }
    if (fileType === "MFSD201") {
      return { "text/plain": [".txt"], "application/zip": [".zip"], "application/x-zip-compressed": [".zip"], "application/octet-stream": [".dbf"], "application/dbase": [".dbf"] };
    }
    if (fileType === "WBR9" || fileType === "MFSD211") {
      // Accept both TXT and Excel for WBR9 and MFSD211
      return { 
        "text/plain": [".txt"],
        "application/zip": [".zip"],
        "application/x-zip-compressed": [".zip"],
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [".xlsx"], 
        "application/vnd.ms-excel": [".xls"]
      };
    }
    if (fileType === "WBR9D") {
      return { 
        "text/plain": [".txt"],
        "application/zip": [".zip"],
        "application/x-zip-compressed": [".zip"]
      };
    }
    if (fileType === "COLUMN_MAPPING" || fileType === "AMFI_MASTER" || fileType === "FLAG_MAPPING") {
      return { 
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [".xlsx"], 
        "application/vnd.ms-excel": [".xls"]
      };
    }
    if (fileType === "PULSE_LABS") {
      return { "text/csv": [".csv"] };
    }
    if (fileType === "WBR2") {
      return { 
        "text/plain": [".txt"],
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [".xlsx"], 
        "application/vnd.ms-excel": [".xls"],
        "application/zip": [".zip"]
      };
    }
    if (fileType === "WBR2A") {
      return { 
        "text/plain": [".txt"],
        "application/zip": [".zip"],
        "application/x-zip-compressed": [".zip"],
        "application/vnd.rar": [".rar"],
        "application/x-rar-compressed": [".rar"],
        "application/octet-stream": [".rar"]
      };
    }
    // Default - multiple formats for flexibility
    return { 
      "text/plain": [".txt"],
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [".xlsx"], 
      "application/vnd.ms-excel": [".xls"],
      "application/zip": [".zip"]
    };
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: getAcceptedTypes(),
    maxFiles: fileType === "MFSD201" ? 50 : 1, // Allow up to 50 DBF files for MFSD201
    multiple: fileType === "MFSD201", // Enable multiple selection for MFSD201
    disabled: isUploading,
  });

  return (
    <div className="relative">
      {/* Download button for Column Mapping */}
      {config.hasDownload && (
        <button
          onClick={async (e) => {
            e.stopPropagation();
            e.preventDefault();
            try {
              const token = localStorage.getItem('token');
              const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}${config.downloadUrl}`, {
                method: 'GET',
                headers: {
                  'Authorization': `Bearer ${token}`
                }
              });
              if (response.ok) {
                const blob = await response.blob();
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = fileType === 'FLAG_MAPPING' ? 'flag_mapping_template.xlsx' : 'column_mapping_guide.xlsx';
                document.body.appendChild(a);
                a.click();
                window.URL.revokeObjectURL(url);
                a.remove();
              } else {
                console.error('Download failed:', response.status);
                alert('Failed to download file. Please try again.');
              }
            } catch (error) {
              console.error('Download error:', error);
              alert('Failed to download file. Please try again.');
            }
          }}
          className="absolute top-2 right-2 z-10 p-2 bg-white rounded-full shadow-md hover:bg-gray-50 transition-colors"
          title="Download existing file"
          type="button"
        >
          <Download className="w-4 h-4 text-gray-600" />
        </button>
      )}
      {/* Download current mapping (for FLAG_MAPPING) */}
      {config.hasCurrentDownload && (
        <button
          onClick={async (e) => {
            e.stopPropagation();
            e.preventDefault();
            try {
              const token = localStorage.getItem('token');
              const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}${config.currentDownloadUrl}`, {
                method: 'GET',
                headers: { 'Authorization': `Bearer ${token}` }
              });
              if (response.ok) {
                const blob = await response.blob();
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = 'flag_mapping_current.xlsx';
                document.body.appendChild(a);
                a.click();
                window.URL.revokeObjectURL(url);
                a.remove();
              } else {
                alert('No current mapping found. Download template first and upload your mappings.');
              }
            } catch (error) {
              alert('Failed to download. Please try again.');
            }
          }}
          className="absolute top-2 right-10 z-10 p-2 bg-white rounded-full shadow-md hover:bg-gray-50 transition-colors"
          title="Download current mapping"
          type="button"
        >
          <FileSpreadsheet className="w-4 h-4 text-violet-600" />
        </button>
      )}
      <div
        {...getRootProps()}
        className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-all ${
          isDragActive 
            ? "border-teal-500 bg-teal-50" 
            : "border-gray-300 hover:border-teal-400 hover:bg-gray-50"
        } ${isUploading ? "opacity-50 cursor-not-allowed" : ""}`}
      >
        <input {...getInputProps()} />
        <div className="flex flex-col items-center gap-3">
          <div className={`p-3 rounded-full ${config.color}`}>
            <Icon className="w-6 h-6" />
          </div>
          <div>
            <p className="font-medium text-gray-800">
              {config.name}
              {config.dbName && <span className="ml-2 text-[10px] font-mono text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded">{config.dbName}</span>}
            </p>
            <p className="text-sm text-gray-500">{config.description}</p>
          </div>
          {isUploading ? (
            <div className="flex items-center gap-2 text-teal-600">
              <Loader2 className="w-4 h-4 animate-spin" />
              <span className="text-sm">Uploading...</span>
            </div>
          ) : (
            <p className="text-sm text-gray-400">
              {isDragActive ? "Drop file here" : `Drag & drop or click to upload (${config.acceptedFiles})`}
            </p>
          )}
          {config.hasDownload && (
            <p className="text-xs text-amber-600">Click download icon (↓) to get existing file</p>
          )}
        </div>
      </div>
    </div>
  );
};

const Masters = () => {
  const { api, user } = useAuth();
  const isSuperAdmin = user?.role === "super_admin" || user?.role === "broker" || user?.role === "platform_owner";
  const [activeTab, setActiveTab] = useState("upload");
  const [uploadingType, setUploadingType] = useState(null);
  const [uploadHistory, setUploadHistory] = useState([]);
  const [deletingJobId, setDeletingJobId] = useState(null);
  const [columnMappingConfigured, setColumnMappingConfigured] = useState(false);
  const [selectedFailedJobs, setSelectedFailedJobs] = useState(new Set());
  const [deletingBulk, setDeletingBulk] = useState(false);
  const [deletingAllData, setDeletingAllData] = useState(false);
  const [referenceDataStats, setReferenceDataStats] = useState({
    bse_scheme_master: { count: 0, last_update: null },
    amfi_master: { count: 0, last_update: null },
    nav: { count: 0, last_update: null }
  });
  const [loadingStats, setLoadingStats] = useState(true);
  const [txnMasterStats, setTxnMasterStats] = useState(null);
  const pollingRef = useRef(null);

  // ── Master regeneration handlers (moved here from Reset DB) ────────────
  const [regeneratingAum, setRegeneratingAum] = useState(false);
  const [regeneratingInvMaster, setRegeneratingInvMaster] = useState(false);
  const [regeneratingTxnMaster, setRegeneratingTxnMaster] = useState(false);
  // Background-job progress tracker. Key = card identifier
  // ("investor" | "aum" | "txn"), value = { jobId, status, stage, startedAt }.
  // The poller below queries /api/upload-jobs/{jobId} every 3s and updates
  // the strip rendered under each Regenerate button.
  const [activeJobs, setActiveJobs] = useState({});
  // Resume any in-progress rebuild started from a previous session so users
  // can navigate away and come back without losing their status bar.
  useEffect(() => {
    try {
      const persisted = JSON.parse(localStorage.getItem("masters_active_jobs") || "{}");
      if (persisted && typeof persisted === "object") {
        // Drop entries older than 60 minutes — any rebuild that took that long
        // has either crashed, been admin-cleared, or completed on a previous
        // tab. Keeping it would leave the user staring at a stuck progress bar.
        const STALE_MS = 60 * 60 * 1000;
        const now = Date.now();
        const fresh = Object.fromEntries(
          Object.entries(persisted).filter(([, v]) => {
            const ts = v?.startedAt;
            return ts && (now - ts) < STALE_MS;
          })
        );
        setActiveJobs(fresh);
      }
    } catch (_) { /* ignore corrupt cache */ }
  }, []);
  useEffect(() => {
    try { localStorage.setItem("masters_active_jobs", JSON.stringify(activeJobs)); } catch (_) {}
  }, [activeJobs]);
  useEffect(() => {
    const liveKeys = Object.entries(activeJobs)
      .filter(([, v]) => v?.jobId && (v.status === "processing" || v.status === "pending"))
      .map(([k]) => k);
    if (liveKeys.length === 0) return;
    let cancelled = false;
    const tick = async () => {
      await Promise.all(liveKeys.map(async (key) => {
        const job = activeJobs[key];
        if (!job?.jobId) return;
        try {
          const r = await api.get(`/upload-jobs/${job.jobId}`);
          if (cancelled) return;
          const j = r?.data || {};
          setActiveJobs(prev => ({
            ...prev,
            [key]: {
              ...prev[key],
              status: j.status || prev[key]?.status,
              stage: j.stage || prev[key]?.stage || "",
              error: j.error_message || j.error || "",
            },
          }));
          if (j.status === "completed") {
            toast.success(`${jobLabelFor(key)} rebuild complete`);
            // Auto-clear after 8s so the strip turns into success state briefly.
            setTimeout(() => setActiveJobs(prev => {
              const next = { ...prev };
              delete next[key];
              return next;
            }), 8000);
          } else if (j.status === "failed") {
            toast.error(`${jobLabelFor(key)} rebuild failed`, {
              description: j.error_message || j.error || "Check logs for details",
            });
            setTimeout(() => setActiveJobs(prev => {
              const next = { ...prev };
              delete next[key];
              return next;
            }), 15000);
          }
        } catch (e) {
          // 404 = the job record was deleted (admin cleanup or stale localStorage
          // pointing to a job ID that no longer exists in upload_jobs).
          // Without this guard the progress strip stays on "Starting…" forever
          // because the poll just silently keeps failing. Clear the entry so
          // the strip disappears and the user can trigger a fresh rebuild.
          if (e?.response?.status === 404) {
            setActiveJobs(prev => {
              const next = { ...prev };
              delete next[key];
              return next;
            });
          }
          // Any other error → network blip, keep polling.
        }
      }));
    };
    const id = setInterval(tick, 3000);
    tick();
    return () => { cancelled = true; clearInterval(id); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(Object.entries(activeJobs).map(([k, v]) => [k, v?.jobId, v?.status]))]);

  const handleRegenerateAumMaster = async () => {
    if (!window.confirm(
      "Rebuild AUM Master?\n\n" +
      "Atomically regenerates aum_master from WBR9 + WBR9D + MFSD211 + MFSD203 using staging + atomic swap.\n\n" +
      "If any source produces 0 rows while raw data exists, the rebuild ABORTS and the existing aum_master is preserved."
    )) return;
    setRegeneratingAum(true);
    try {
      const response = await api.post("/aum-master/generate");
      if (response.data?.job_id) {
        toast.success("AUM Master rebuild started", { description: "Running in background." });
        setActiveJobs(prev => ({
          ...prev,
          aum: { jobId: response.data.job_id, status: "processing", stage: "Starting…", startedAt: Date.now() },
        }));
      } else {
        toast.error("Failed to start AUM Master rebuild");
      }
    } catch (error) {
      toast.error("Failed to start AUM Master rebuild", { description: error?.response?.data?.detail || String(error) });
    } finally {
      setRegeneratingAum(false);
    }
  };

  const handleRegenerateInvestorMaster = async () => {
    if (!window.confirm(
      "Regenerate Investor Master?\n\n" +
      "Field-level merge upsert from WBR9 + WBR9D (CAMS) + MFSD211 (Karvy). Only folios in today's feed are touched; everything else stays as-is. Typical runtime 2–10 minutes."
    )) return;
    setRegeneratingInvMaster(true);
    try {
      const response = await api.post("/common-investor-master/generate");
      const jobId = response.data?.job_id;
      if (jobId) {
        toast.success("Investor Master rebuild started", { description: "Running in background." });
        setActiveJobs(prev => ({
          ...prev,
          investor: { jobId, status: "processing", stage: "Starting…", startedAt: Date.now() },
        }));
      } else {
        toast.success("Investor Master rebuild started", { description: "Running in background." });
      }
    } catch (error) {
      toast.error("Failed to start rebuild", { description: error?.response?.data?.detail || String(error) });
    } finally {
      setRegeneratingInvMaster(false);
    }
  };

  const handleRegenerateTxnMaster = async () => {
    if (!window.confirm(
      "Regenerate Transaction Master?\n\n" +
      "Atomic rebuild from WBR2 + WBR2A (CAMS) + MFSD201 (Karvy). Snapshot+restore on failure."
    )) return;
    setRegeneratingTxnMaster(true);
    try {
      const response = await api.post("/transaction-master/generate");
      if (response.data?.success || response.data?.job_id) {
        toast.success("Transaction Master rebuild started", { description: "Running in background." });
        if (response.data?.job_id) {
          setActiveJobs(prev => ({
            ...prev,
            txn: { jobId: response.data.job_id, status: "processing", stage: "Starting…", startedAt: Date.now() },
          }));
        }
      } else {
        toast.error("Failed to start rebuild", { description: response.data?.detail });
      }
    } catch (error) {
      toast.error("Failed to start rebuild", { description: error?.response?.data?.detail || String(error) });
    } finally {
      setRegeneratingTxnMaster(false);
    }
  };

  // Label used by the background-job polling effect when surfacing toasts.
  const jobLabelFor = (key) => ({ investor: "Investor Master", aum: "AUM Master", txn: "Transaction Master" }[key] || "Job");

  // Stage-string → 0..100 estimate. The backend doesn't emit a numeric
  // percentage so we infer one from the recognised milestones; falls back
  // to an indeterminate-style 30% wash for unknown stages.
  const stageProgress = (stage = "") => {
    const s = String(stage).toLowerCase();
    if (!s || s === "starting…") return 5;
    if (s.includes("mfsd211")) return 35;
    if (s.includes("wbr9d")) return 55;
    if (s.includes("wbr9")) return 75;
    if (s.includes("merge") || s.includes("upsert")) return 88;
    if (s.includes("index") || s.includes("audit")) return 95;
    if (s.includes("complet")) return 100;
    return 30;
  };

  const JobStatusStrip = ({ jobKey }) => {
    const job = activeJobs[jobKey];
    if (!job?.jobId) return null;
    const pct = job.status === "completed" ? 100 : job.status === "failed" ? 100 : stageProgress(job.stage);
    const elapsedSec = Math.max(1, Math.floor((Date.now() - (job.startedAt || Date.now())) / 1000));
    const mm = String(Math.floor(elapsedSec / 60)).padStart(2, "0");
    const ss = String(elapsedSec % 60).padStart(2, "0");
    // Static tailwind classes only — JIT purges dynamic class names so we
    // can't interpolate `bg-${tone}-50` etc.
    const toneClasses = job.status === "completed"
      ? "border-emerald-200 bg-emerald-50/70 text-emerald-700"
      : job.status === "failed"
        ? "border-rose-200 bg-rose-50/70 text-rose-700"
        : "border-blue-200 bg-blue-50/70 text-blue-700";
    const Icon = job.status === "completed" ? CheckCircle2 : job.status === "failed" ? XCircle : Loader2;
    const iconClasses = job.status === "completed" || job.status === "failed" ? "h-3.5 w-3.5" : "h-3.5 w-3.5 animate-spin";
    return (
      <div className={`mt-2 rounded border p-2 text-xs ${toneClasses}`} data-testid={`job-status-${jobKey}`}>
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 font-medium">
            <Icon className={iconClasses} />
            <span>{jobLabelFor(jobKey)}: {job.stage || "Working…"}</span>
          </div>
          <div className="text-gray-500 font-mono">{mm}:{ss}</div>
        </div>
        <Progress value={pct} className="mt-1.5 h-1.5" />
        {job.error && (
          <div className="mt-1 text-rose-700 break-all">Error: {job.error}</div>
        )}
      </div>
    );
  };

  const fetchTxnMasterStats = async () => {
    try {
      const response = await api.get("/transaction-master/stats");
      setTxnMasterStats(response.data);
    } catch (error) {
      console.error("Failed to fetch txn master stats:", error);
    }
  };

  const downloadUnmapped = async (field) => {
    try {
      toast.info(`Preparing download for missing ${field.replace('_', ' ')}...`);
      const response = await api.get(`/transaction-master/unmapped/export`, {
        params: { field },
        responseType: 'blob',
      });
      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `transaction_master_missing_${field}_${new Date().toISOString().slice(0,10)}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Download started");
    } catch (error) {
      if (error.response && error.response.status === 404) {
        toast.info("No unmapped rows found");
      } else {
        console.error("Failed to download unmapped rows:", error);
        toast.error("Download failed");
      }
    }
  };

  const fetchReferenceDataStats = async () => {
    try {
      setLoadingStats(true);
      const response = await api.get("/reference-data/stats");
      setReferenceDataStats(response.data);
    } catch (error) {
      console.error("Failed to fetch reference data stats:", error);
    } finally {
      setLoadingStats(false);
    }
  };

  const checkColumnMappingStatus = async () => {
    try {
      const response = await api.get("/column-mapping/config");
      setColumnMappingConfigured(response.data.total_fields > 0);
    } catch (error) {
      // If endpoint fails, check if file exists via download endpoint
      setColumnMappingConfigured(true); // Assume configured if can't check
    }
  };

  const fetchUploadHistory = async () => {
    try {
      const [mainResp, sbResp] = await Promise.all([
        api.get("/upload-jobs"),
        api.get("/subbroker/upload-history").catch(() => ({ data: { batches: [] } })),
      ]);
      const mainJobs = mainResp.data || [];
      const sbBatches = (sbResp.data?.batches) || [];

      // Map sub broker batches to the same shape as upload_jobs rows
      const sbJobs = sbBatches.map((b) => {
        const total = b.total_records || 0;
        const fails = b.failure_count || 0;
        const succ = b.success_count || 0;
        const status =
          fails === total && total > 0
            ? "failed"
            : fails > 0
            ? "completed"  // partial success — still shown as completed with warning
            : "completed";
        return {
          id: b.id,
          file_type: "SUB_BROKER",
          file_name: b.file_name || "subbroker_upload.xlsx",
          status,
          total_rows: total,
          records_processed: succ,
          created_at: b.upload_date,
          error: fails > 0 ? `${fails} row(s) failed` : null,
          is_subbroker_batch: true,
          error_file_url: fails > 0 ? `/subbroker/upload-history/${b.id}/errors` : null,
          uploaded_by_name: b.uploaded_by_name || null,
        };
      });

      const merged = [...mainJobs, ...sbJobs].sort((a, b) => {
        const da = new Date(a.created_at || 0).getTime();
        const dbt = new Date(b.created_at || 0).getTime();
        return dbt - da;
      });

      setUploadHistory(merged);
      return merged;
    } catch (error) {
      console.error("Failed to fetch upload history:", error);
      return [];
    }
  };

  const handleDeleteAllData = async () => {
    const confirmText = "DELETE ALL DATA";
    const userInput = window.prompt(
      `⚠️ WARNING: This will permanently delete ALL data including:\n\n` +
      `• All Client Holdings (CAMS & Karvy)\n` +
      `• All Transactions\n` +
      `• All KYC Mismatches\n` +
      `• All Upload History\n` +
      `• Scheme Master & NAV Data\n` +
      `• All Cronjob Data\n\n` +
      `Type "${confirmText}" to confirm:`
    );
    
    if (userInput !== confirmText) {
      if (userInput !== null) {
        toast.error("Deletion cancelled", {
          description: "Text did not match. No data was deleted.",
        });
      }
      return;
    }
    
    setDeletingAllData(true);
    try {
      const response = await api.delete("/data/all");
      toast.success("All data deleted successfully", {
        description: response.data.message,
      });
      fetchUploadHistory();
      checkColumnMappingStatus();
    } catch (error) {
      console.error("Failed to delete all data:", error);
      toast.error("Failed to delete all data", {
        description: error.response?.data?.detail || "An error occurred",
      });
    } finally {
      setDeletingAllData(false);
    }
  };

  // Initial fetch
  useEffect(() => {
    fetchUploadHistory();
    checkColumnMappingStatus();
    fetchReferenceDataStats();
    fetchTxnMasterStats();
  }, []);

  // Poll for updates when there are processing jobs
  useEffect(() => {
    const hasProcessingJobs = uploadHistory.some(job => job.status === "processing");
    
    if (hasProcessingJobs) {
      // Start polling every 2 seconds
      pollingRef.current = setInterval(async () => {
        const jobs = await fetchUploadHistory();
        const stillProcessing = jobs.some(job => job.status === "processing");
        
        if (!stillProcessing) {
          // Stop polling when done
          clearInterval(pollingRef.current);
          pollingRef.current = null;
        }
      }, 2000);
    }
    
    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
      }
    };
  }, [uploadHistory]);

  const handleUpload = async (fileOrFiles, fileType, isMultiple = false) => {
    setUploadingType(fileType);
    
    try {
      // Handle multiple DBF files for MFSD201
      if (fileType === "MFSD201" && isMultiple && Array.isArray(fileOrFiles)) {
        const formData = new FormData();
        fileOrFiles.forEach(file => {
          formData.append("files", file);
        });
        formData.append("password", FILE_TYPES.MFSD201.defaultPassword);
        
        await api.post("/upload/mfsd201/multiple", formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        
        toast.success(`Uploading ${fileOrFiles.length} MFSD201 files!`, {
          description: "Processing in background...",
        });
      } else {
        // Single file upload
        const file = Array.isArray(fileOrFiles) ? fileOrFiles[0] : fileOrFiles;
        const formData = new FormData();
        formData.append("file", file);
        formData.append("file_type", fileType);

        // Use special endpoint for MFSD201 with password
        if (fileType === "MFSD201") {
          const password = FILE_TYPES.MFSD201.defaultPassword;
          formData.append("password", password);
          await api.post("/upload/mfsd201", formData, {
            headers: { "Content-Type": "multipart/form-data" },
          });
        } else if (fileType === "WBR2") {
          // WBR2 uses chunked upload for large files to avoid proxy timeout
          const password = FILE_TYPES.WBR2.defaultPassword;
          const filename = file.name;
          const isZip = filename.toLowerCase().endsWith('.zip');
          const CHUNK_SIZE = 4 * 1024 * 1024; // 4MB chunks
          
          if (file.size > CHUNK_SIZE) {
            // Chunked upload
            const totalChunks = Math.ceil(file.size / CHUNK_SIZE);
            toast.info(`Uploading ${filename} in ${totalChunks} chunks...`);
            
            // 1. Init session
            const initResp = await api.post(`/upload/init-chunked?filename=${encodeURIComponent(filename)}&total_size=${file.size}&total_chunks=${totalChunks}`);
            const uploadId = initResp.data.upload_id;
            
            // 2. Upload chunks
            for (let i = 0; i < totalChunks; i++) {
              const start = i * CHUNK_SIZE;
              const end = Math.min(start + CHUNK_SIZE, file.size);
              const chunk = file.slice(start, end);
              const chunkForm = new FormData();
              chunkForm.append("file", chunk, `chunk_${i}`);
              await api.post(`/upload/chunk?upload_id=${uploadId}&chunk_number=${i}`, chunkForm, {
                headers: { "Content-Type": "multipart/form-data" },
              });
            }
            
            // 3. Complete
            const zipPwd = isZip ? `&zip_password=${encodeURIComponent(password)}` : '';
            await api.post(`/upload/complete-chunked?upload_id=${uploadId}&file_type=WBR2${zipPwd}`);
          } else {
            // Small file - direct upload
            const queryParams = isZip ? `?report_type=WBR2&zip_password=${encodeURIComponent(password)}` : `?report_type=WBR2`;
            await api.post(`/cronjobs/cams-wbr2/upload${queryParams}`, formData, {
              headers: { "Content-Type": "multipart/form-data" },
            });
          }
        } else if (fileType === "WBR2A") {
          // WBR2A uses special endpoint for zip/rar with multiple yearly files
          await api.post(`/cronjobs/cams-wbr2a/upload?report_type=WBR2A`, formData, {
            headers: { "Content-Type": "multipart/form-data" },
          });
        } else if (fileType === "MFSD211" && file.name.toLowerCase().endsWith('.zip')) {
          // MFSD211 zip files need password
          const password = FILE_TYPES.MFSD211.defaultPassword;
          formData.append("password", password);
          await api.post("/upload/master", formData, {
            headers: { "Content-Type": "multipart/form-data" },
          });
        } else if (fileType === "MFSD203") {
          // MFSD203 supports .txt/.zip/.xlsx/.xls/.csv — always route
          // via /upload/master with password so backend can dispatch
          // to process_mfsd203_zip_file / process_mfsd203_txt_file /
          // process_mfsd203_file based on filename extension.
          const password = FILE_TYPES.MFSD203.defaultPassword;
          formData.append("password", password);
          await api.post("/upload/master", formData, {
            headers: { "Content-Type": "multipart/form-data" },
          });
        } else if ((fileType === "WBR9" || fileType === "WBR9D") && file.name.toLowerCase().endsWith('.zip')) {
          // WBR9/WBR9D zip files need password
          const password = FILE_TYPES[fileType].defaultPassword;
          formData.append("password", password);
          await api.post("/upload/master", formData, {
            headers: { "Content-Type": "multipart/form-data" },
          });
        } else if (fileType === "FLAG_MAPPING") {
          // Flag mapping uses dedicated endpoint
          await api.post("/flag-mapping/upload", formData, {
            headers: { "Content-Type": "multipart/form-data" },
          });
        } else if (fileType === "COLUMN_MAPPING") {
          // Column mapping guide → column_mapping_guide DB
          await api.post("/column-mapping/upload", formData, {
            headers: { "Content-Type": "multipart/form-data" },
          });
        } else if (fileType === "TXN_MASTER_TEMPLATE") {
          // Transaction master mapping guide → txn_master_mapping_guide DB
          await api.post("/transaction-master/template/upload", formData, {
            headers: { "Content-Type": "multipart/form-data" },
          });
        } else if (fileType === "BSE_SCHEME" || fileType === "AMFI_MASTER" || fileType === "BSE_NAV" || fileType === "BENCHMARK" || fileType === "PULSE_LABS") {
          // Generic reference-data master upload (BSE Scheme .txt, AMFI master, BSE NAV, etc.)
          await api.post("/upload/master", formData, {
            headers: { "Content-Type": "multipart/form-data" },
          });
        } else {
        }
        
        toast.success(`${FILE_TYPES[fileType].name} upload started!`, {
          description: "Processing in background...",
        });
      }
      
      // Switch to history tab to show progress
      setActiveTab("history");
      
      // Fetch immediately to show the new job
      await fetchUploadHistory();
      
    } catch (error) {
      toast.error(`Failed to upload ${FILE_TYPES[fileType].name}`, {
        description: error.response?.data?.detail || "Please check file format and try again",
      });
    } finally {
      setUploadingType(null);
    }
  };

  const handleClearData = async (collection) => {
    if (!window.confirm(`Are you sure you want to clear all data from ${collection}? This cannot be undone.`)) {
      return;
    }
    
    try {
      await api.delete(`/data/${collection}`);
      toast.success(`${collection} cleared successfully`);
    } catch (error) {
      toast.error(`Failed to clear ${collection}`);
    }
  };

  const handleDeleteUpload = async (jobId, fileType) => {
    if (!window.confirm(`Are you sure you want to delete this upload and its associated data? This cannot be undone.`)) {
      return;
    }
    
    setDeletingJobId(jobId);
    try {
      await api.delete(`/upload-jobs/${jobId}`);
      toast.success("Upload deleted successfully");
      fetchUploadHistory();
    } catch (error) {
      toast.error("Failed to delete upload");
    } finally {
      setDeletingJobId(null);
    }
  };

  // Get all failed job IDs for "select all" functionality (excluding sub broker batches)
  const failedJobIds = uploadHistory.filter(job => job.status === "failed" && !job.is_subbroker_batch).map(job => job.id);

  const toggleFailedJobSelection = (jobId) => {
    setSelectedFailedJobs(prev => {
      const newSet = new Set(prev);
      if (newSet.has(jobId)) {
        newSet.delete(jobId);
      } else {
        newSet.add(jobId);
      }
      return newSet;
    });
  };

  const toggleSelectAllFailed = () => {
    if (selectedFailedJobs.size === failedJobIds.length) {
      setSelectedFailedJobs(new Set());
    } else {
      setSelectedFailedJobs(new Set(failedJobIds));
    }
  };

  const handleBulkDeleteFailed = async () => {
    if (selectedFailedJobs.size === 0) return;
    
    if (!window.confirm(`Are you sure you want to delete ${selectedFailedJobs.size} failed upload(s)? This cannot be undone.`)) {
      return;
    }
    
    setDeletingBulk(true);
    try {
      const jobIds = Array.from(selectedFailedJobs);
      await api.post("/upload-jobs/bulk-delete", { job_ids: jobIds });
      toast.success(`${jobIds.length} failed upload(s) deleted successfully`);
      setSelectedFailedJobs(new Set());
      fetchUploadHistory();
    } catch (error) {
      toast.error("Failed to delete uploads", {
        description: error.response?.data?.detail || "An error occurred",
      });
    } finally {
      setDeletingBulk(false);
    }
  };

  return (
    <div className="p-6 space-y-6" data-testid="masters-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Masters</h1>
          <p className="text-gray-500 mt-1">Upload &amp; manage master data files, plus account-creation reference tables</p>
        </div>
        <div className="flex items-center gap-3">
          <Button 
            variant="outline" 
            onClick={fetchUploadHistory}
            data-testid="masters-refresh-button"
          >
            <RefreshCcw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Top-level split — RTA Master (existing RTA upload/history flow) vs
          Account Master (gender + country reference tables). Larger,
          teal-accented pills to visually distinguish parent tabs from
          the smaller subtabs (Upload Files / Upload History / Gender /
          Country) rendered inside each. */}
      <Tabs defaultValue="rta" data-testid="masters-top-tabs">
        <TabsList className="bg-teal-50 p-1 h-11 gap-1">
          <TabsTrigger
            value="rta"
            data-testid="rta-master-tab"
            className="px-5 py-1.5 text-sm font-semibold text-teal-800 data-[state=active]:bg-teal-600 data-[state=active]:text-white data-[state=active]:shadow rounded-md"
          >
            RTA Master
          </TabsTrigger>
          <TabsTrigger
            value="account"
            data-testid="account-master-tab"
            className="px-5 py-1.5 text-sm font-semibold text-teal-800 data-[state=active]:bg-teal-600 data-[state=active]:text-white data-[state=active]:shadow rounded-md"
          >
            Account Master
          </TabsTrigger>
        </TabsList>

        <TabsContent value="rta" className="mt-4 space-y-6">
          {/* Existing RTA upload / history sub-tabs — slate/neutral so
              they read as children of the RTA Master parent above. */}
          <Tabs value={activeTab} onValueChange={setActiveTab} data-testid="masters-tabs">
            <TabsList className="bg-slate-100 h-9 gap-0.5">
              <TabsTrigger
                value="upload"
                data-testid="upload-files-tab"
                className="px-3 py-1 text-xs font-medium text-slate-600 data-[state=active]:bg-white data-[state=active]:text-slate-900 data-[state=active]:shadow-sm"
              >
                Upload Files
              </TabsTrigger>
              <TabsTrigger
                value="history"
                data-testid="upload-history-tab"
                className="px-3 py-1 text-xs font-medium text-slate-600 data-[state=active]:bg-white data-[state=active]:text-slate-900 data-[state=active]:shadow-sm"
              >
                Upload History
              </TabsTrigger>
            </TabsList>

            <TabsContent value="upload" className="mt-4" data-testid="upload-files-content">
          {/* Reference Data Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            {/* BSE Scheme Master Card */}
            <Card className="border-l-4 border-l-orange-500">
              <CardContent className="pt-4">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-500">BSE Scheme Master</p>
                    <p className="text-2xl font-bold text-gray-800 mt-1">
                      {loadingStats ? (
                        <Loader2 className="w-5 h-5 animate-spin inline" />
                      ) : (
                        referenceDataStats.bse_scheme_master?.count?.toLocaleString() || 0
                      )}
                    </p>
                    <p className="text-xs text-gray-400 mt-1">records</p>
                  </div>
                  <div className="p-2 bg-orange-100 rounded-lg">
                    <Database className="w-5 h-5 text-orange-600" />
                  </div>
                </div>
                <div className="mt-3 pt-3 border-t border-gray-100">
                  <div className="flex items-center gap-1 text-xs text-gray-500">
                    <Calendar className="w-3 h-3" />
                    <span>Last Update: </span>
                    <span className="font-medium text-gray-700">
                      {referenceDataStats.bse_scheme_master?.last_update 
                        ? new Date(referenceDataStats.bse_scheme_master.last_update).toLocaleDateString('en-IN', {day: '2-digit', month: 'short', year: 'numeric'})
                        : 'Never'}
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Scheme Data (AMFI) Card */}
            <Card className="border-l-4 border-l-teal-500">
              <CardContent className="pt-4">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-500">Scheme Data</p>
                    <p className="text-2xl font-bold text-gray-800 mt-1">
                      {loadingStats ? (
                        <Loader2 className="w-5 h-5 animate-spin inline" />
                      ) : (
                        referenceDataStats.amfi_master?.count?.toLocaleString() || 0
                      )}
                    </p>
                    <p className="text-xs text-gray-400 mt-1">records</p>
                  </div>
                  <div className="p-2 bg-teal-100 rounded-lg">
                    <FileSpreadsheet className="w-5 h-5 text-teal-600" />
                  </div>
                </div>
                <div className="mt-3 pt-3 border-t border-gray-100">
                  <div className="flex items-center gap-1 text-xs text-gray-500">
                    <Calendar className="w-3 h-3" />
                    <span>Last Update: </span>
                    <span className="font-medium text-gray-700">
                      {referenceDataStats.amfi_master?.last_update 
                        ? new Date(referenceDataStats.amfi_master.last_update).toLocaleDateString('en-IN', {day: '2-digit', month: 'short', year: 'numeric'})
                        : 'Never'}
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* NAV Card */}
            <Card className="border-l-4 border-l-indigo-500">
              <CardContent className="pt-4">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-500">NAV Data</p>
                    <p className="text-2xl font-bold text-gray-800 mt-1">
                      {loadingStats ? (
                        <Loader2 className="w-5 h-5 animate-spin inline" />
                      ) : (
                        referenceDataStats.nav?.count?.toLocaleString() || 0
                      )}
                    </p>
                    <p className="text-xs text-gray-400 mt-1">records</p>
                  </div>
                  <div className="p-2 bg-indigo-100 rounded-lg">
                    <TrendingUp className="w-5 h-5 text-indigo-600" />
                  </div>
                </div>
                <div className="mt-3 pt-3 border-t border-gray-100">
                  <div className="flex items-center gap-1 text-xs text-gray-500">
                    <Calendar className="w-3 h-3" />
                    <span>Last Update: </span>
                    <span className="font-medium text-gray-700">
                      {referenceDataStats.nav?.last_update 
                        ? new Date(referenceDataStats.nav.last_update).toLocaleDateString('en-IN', {day: '2-digit', month: 'short', year: 'numeric'})
                        : 'Never'}
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Column Mapping Card */}
            <Card className="border-l-4 border-l-purple-500">
              <CardContent className="pt-4">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-500">Column Mapping</p>
                    <p className="text-2xl font-bold text-gray-800 mt-1">
                      {loadingStats ? (
                        <Loader2 className="w-5 h-5 animate-spin inline" />
                      ) : (
                        referenceDataStats.column_mapping?.count?.toLocaleString() || 0
                      )}
                    </p>
                    <p className="text-xs text-gray-400 mt-1">fields configured</p>
                  </div>
                  <div className="p-2 bg-purple-100 rounded-lg">
                    <FileText className="w-5 h-5 text-purple-600" />
                  </div>
                </div>
                <div className="mt-3 pt-3 border-t border-gray-100">
                  <div className="flex items-center gap-1 text-xs text-gray-500">
                    <Calendar className="w-3 h-3" />
                    <span>Last Update: </span>
                    <span className="font-medium text-gray-700">
                      {referenceDataStats.column_mapping?.last_update 
                        ? new Date(referenceDataStats.column_mapping.last_update).toLocaleDateString('en-IN', {day: '2-digit', month: 'short', year: 'numeric'})
                        : 'Never'}
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardHeader>
              <CardTitle>Upload Master Files</CardTitle>
            </CardHeader>
            <CardContent>
              {/* Step 1: Investor Master & AUM Files (was Step 2) */}
              <div className="mb-6">
                <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
                  <div className="flex items-center gap-2">
                    <span className="bg-blue-500 text-white text-xs font-bold px-2 py-1 rounded">STEP 1</span>
                    <h3 className="font-semibold text-gray-800">Upload Investor Master & AUM Files</h3>
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <Button
                      onClick={handleRegenerateInvestorMaster}
                      disabled={regeneratingInvMaster}
                      variant="outline"
                      size="sm"
                      className="border-emerald-200 text-emerald-700 hover:bg-emerald-50"
                      data-testid="regenerate-investor-master-button"
                      title="Atomic rebuild of investor_master from WBR9 + WBR9D (CAMS) + MFSD211 (Karvy)."
                    >
                      {regeneratingInvMaster
                        ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
                        : <RefreshCw className="h-3.5 w-3.5 mr-1" />}
                      Regenerate Investor Master
                    </Button>
                    <Button
                      onClick={handleRegenerateAumMaster}
                      disabled={regeneratingAum}
                      variant="outline"
                      size="sm"
                      className="border-violet-200 text-violet-700 hover:bg-violet-50"
                      data-testid="regenerate-aum-master-button"
                      title="Safely rebuild aum_master using staging + atomic swap."
                    >
                      {regeneratingAum
                        ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
                        : <RefreshCw className="h-3.5 w-3.5 mr-1" />}
                      Regenerate AUM Master
                    </Button>
                  </div>
                </div>
                <JobStatusStrip jobKey="investor" />
                <JobStatusStrip jobKey="aum" />
                <p className="text-sm text-gray-500 mb-3">
                  These files create unique clients, sub-accounts, balance units (AUM), and are used for mismatch & missing data analysis.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  {["MFSD211", "MFSD203", "WBR9", "WBR9D"].map((key) => (
                    <FileUploadZone
                      key={key}
                      fileType={key}
                      onUpload={handleUpload}
                      isUploading={uploadingType === key}
                    />
                  ))}
                </div>
              </div>

              {/* Step 2: Transaction Files (was Step 3) */}
              <div className="mb-6">
                <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
                  <div className="flex items-center gap-2">
                    <span className="bg-purple-500 text-white text-xs font-bold px-2 py-1 rounded">STEP 2</span>
                    <h3 className="font-semibold text-gray-800">Upload Transaction Files</h3>
                  </div>
                  <Button
                    onClick={handleRegenerateTxnMaster}
                    disabled={regeneratingTxnMaster}
                    variant="outline"
                    size="sm"
                    className="border-cyan-200 text-cyan-700 hover:bg-cyan-50"
                    data-testid="regenerate-transaction-master-button"
                    title="Atomic rebuild of transaction_master from WBR2 + WBR2A (CAMS) + MFSD201 (Karvy)."
                  >
                    {regeneratingTxnMaster
                      ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
                      : <RefreshCw className="h-3.5 w-3.5 mr-1" />}
                    Regenerate Transaction Master
                  </Button>
                </div>
                <JobStatusStrip jobKey="txn" />
                <p className="text-sm text-gray-500 mb-3">
                  These files capture transaction details for Unit Reconciliation (Transactions vs AUM comparison).
                </p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {["MFSD201", "WBR2", "WBR2A"].map((key) => (
                    <FileUploadZone
                      key={key}
                      fileType={key}
                      onUpload={handleUpload}
                      isUploading={uploadingType === key}
                    />
                  ))}
                </div>
                <p className="text-xs text-gray-400 mt-2">
                  <strong>WBR2A:</strong> Supports ZIP files containing multiple yearly WBR2A text files (e.g., WBR2A_2024.txt, WBR2A_2025.txt)
                </p>
              </div>

              {/* Optional: Reference Data Files */}
              <div className="mb-6">
                <div className="flex items-center gap-2 mb-3">
                  <span className="bg-gray-500 text-white text-xs font-bold px-2 py-1 rounded">OPTIONAL</span>
                  <h3 className="font-semibold text-gray-800">Reference Data Files</h3>
                </div>
                <p className="text-sm text-gray-400 mb-3">
                  BSE Scheme Master, AMFI Master, and NAV are auto-downloaded via Cronjobs. Upload manually only if needed.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  {["BSE_SCHEME", "AMFI_MASTER", "BSE_NAV", "PULSE_LABS", "COLUMN_MAPPING", "FLAG_MAPPING", "TXN_MASTER_TEMPLATE"].map((key) => (
                    <FileUploadZone
                      key={key}
                      fileType={key}
                      onUpload={(file) => {
                        handleUpload(file, key);
                        // Refresh stats after upload
                        setTimeout(fetchReferenceDataStats, 3000);
                        if (key === "FLAG_MAPPING") {
                          setTimeout(fetchTxnMasterStats, 5000);
                        }
                      }}
                      isUploading={uploadingType === key}
                    />
                  ))}
                </div>

                {/* Unmapped counts for Transaction Flag Mapping */}
                {txnMasterStats && txnMasterStats.total > 0 && (
                  <div className="mt-3 p-3 bg-gray-50 rounded-lg border border-gray-200" data-testid="flag-mapping-stats">
                    <p className="text-xs font-medium text-gray-600 mb-1">
                      Transaction Master Mapping Status
                      <span className="ml-2 text-gray-400 font-normal">(click a count to download the unmapped rows)</span>
                    </p>
                    <div className="flex gap-4 text-xs">
                      <span className="text-gray-500">Total: <strong className="text-gray-700">{txnMasterStats.total?.toLocaleString()}</strong></span>
                      <button
                        type="button"
                        data-testid="download-missing-description"
                        disabled={!txnMasterStats.unmapped_count}
                        onClick={() => downloadUnmapped('description')}
                        className={`${txnMasterStats.unmapped_count > 0 ? "text-red-600 hover:text-red-700 hover:underline cursor-pointer" : "text-green-600"} disabled:cursor-not-allowed disabled:no-underline`}
                      >
                        Missing Description: <strong>{txnMasterStats.unmapped_count?.toLocaleString() || 0}</strong>
                      </button>
                      <button
                        type="button"
                        data-testid="download-missing-treatment"
                        disabled={!txnMasterStats.unmapped_treatment_count}
                        onClick={() => downloadUnmapped('treatment')}
                        className={`${txnMasterStats.unmapped_treatment_count > 0 ? "text-red-600 hover:text-red-700 hover:underline cursor-pointer" : "text-green-600"} disabled:cursor-not-allowed disabled:no-underline`}
                      >
                        Missing Treatment: <strong>{txnMasterStats.unmapped_treatment_count?.toLocaleString() || 0}</strong>
                      </button>
                      <button
                        type="button"
                        data-testid="download-missing-txn-flag"
                        disabled={!txnMasterStats.unmapped_txn_flag_count}
                        onClick={() => downloadUnmapped('txn_flag')}
                        className={`${txnMasterStats.unmapped_txn_flag_count > 0 ? "text-red-600 hover:text-red-700 hover:underline cursor-pointer" : "text-green-600"} disabled:cursor-not-allowed disabled:no-underline`}
                      >
                        Missing Txn Flag: <strong>{txnMasterStats.unmapped_txn_flag_count?.toLocaleString() || 0}</strong>
                      </button>
                    </div>
                  </div>
                )}
              </div>
              
              {/* File Format Info */}
              <div className="mt-6 p-4 bg-gray-50 rounded-lg">
                <h4 className="font-medium text-gray-800 mb-2">Data Flow:</h4>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li><strong>Step 1 - Investor Master & AUM:</strong> MFSD211/WBR9/WBR9D (clients, folios) + MFSD203 (balance units/AUM)</li>
                  <li><strong>Step 2 - Transactions:</strong> MFSD201/WBR2/WBR2A (transaction details for Unit Reconciliation)</li>
                  <li><strong>Optional:</strong> BSE Scheme Master, AMFI Master, Historical NAVs (auto-downloaded via Cronjobs)</li>
                  <li><strong>Column Mapping Guide:</strong> Available in Downloads section on sidebar</li>
                </ul>
              </div>
            </CardContent>
          </Card>

          {/* Profile — Sub Broker bulk upload module (super-admin only) */}
          <div className="mt-6 space-y-3" data-testid="masters-profile-section">
            <div className="flex items-center gap-2 pt-2 border-t border-gray-200 pt-4">
              <Network className="w-5 h-5 text-teal-700" />
              <h2 className="text-xl font-semibold text-gray-800">Profile</h2>
            </div>
            <p className="text-sm text-gray-500">Associate onboarding via XLSX/CSV. Full Associate management lives in the <Link to="/sub-brokers/dashboard" className="text-teal-600 hover:underline">Associates</Link> sidebar entry.</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <ProfileLink
                to="/sub-brokers/bulk-upload"
                icon={Upload}
                label="Associate Master Upload"
                desc="Onboard associates via XLSX / CSV (100-column Associate Master)"
                testid="profile-link-bulk-upload"
                downloadUrl="/api/subbroker/template"
                downloadFileName="associate_master_template.xlsx"
                downloadTitle="Download Associate Master upload template"
              />
              <ProfileLink
                to="/sub-brokers/client-mapping"
                icon={Users}
                label="Client Mapping"
                desc="Map PAN-level clients to associates (manual or bulk upload)"
                testid="profile-link-client-mapping"
                downloadUrl="/api/subbroker/client-master/export"
                downloadFileName="client_master.xlsx"
                downloadTitle="Download Client Master (PAN · Name · Guardian PAN · Associate Code)"
              />
              {isSuperAdmin && (
                <ProfileLink
                  to="/masters/report-prefs-upload"
                  icon={Bell}
                  label="Report Preference Upload"
                  desc="Bulk-update reporting preferences for every client (frequency · which reports · email)"
                  testid="profile-link-report-prefs"
                  downloadUrl="/api/reporting-preferences/clients-template"
                  downloadFileName="client_reporting_preferences_template.xlsx"
                  downloadTitle="Download client reporting preferences template (every PAN, current settings)"
                />
              )}
              {isSuperAdmin && (
                <ProfileLink
                  to="/masters/family-mapping-upload"
                  icon={Users}
                  label="Family Mapping Upload"
                  desc="Tag a Group Head PAN to each family member's PAN. Group Heads see all family members in their portfolio."
                  testid="profile-link-family-mapping"
                  downloadUrl="/api/family-mapping/export"
                  downloadFileName="family_mapping.xlsx"
                  downloadTitle="Download current family mapping (PAN · Name · Group Head PAN · Group Head Name)"
                />
              )}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="history" className="mt-4" data-testid="upload-history-content">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Upload History</CardTitle>
              {failedJobIds.length > 0 && (
                <div className="flex items-center gap-3">
                  <span className="text-sm text-gray-500">
                    {selectedFailedJobs.size > 0 
                      ? `${selectedFailedJobs.size} of ${failedJobIds.length} failed selected`
                      : `${failedJobIds.length} failed upload(s)`
                    }
                  </span>
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={handleBulkDeleteFailed}
                    disabled={selectedFailedJobs.size === 0 || deletingBulk}
                    data-testid="bulk-delete-failed-btn"
                  >
                    {deletingBulk ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Trash2 className="w-4 h-4 mr-2" />
                    )}
                    Delete Selected ({selectedFailedJobs.size})
                  </Button>
                </div>
              )}
            </CardHeader>
            <CardContent>
              {uploadHistory.length === 0 ? (
                <div className="text-center py-8 text-gray-500" data-testid="no-history-message">
                  No upload history yet
                </div>
              ) : (
                <Table data-testid="upload-history-table">
                  <TableHeader>
                    <TableRow>
                      {failedJobIds.length > 0 && (
                        <TableHead className="w-12">
                          <Checkbox
                            checked={selectedFailedJobs.size === failedJobIds.length && failedJobIds.length > 0}
                            onCheckedChange={toggleSelectAllFailed}
                            aria-label="Select all failed uploads"
                            data-testid="select-all-failed-checkbox"
                          />
                        </TableHead>
                      )}
                      <TableHead>File Type</TableHead>
                      <TableHead>File Name</TableHead>
                      <TableHead>Progress</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Uploaded At</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {uploadHistory.map((job) => (
                      <TableRow key={job.id}>
                        {failedJobIds.length > 0 && (
                          <TableCell className="w-12">
                            {job.status === "failed" && !job.is_subbroker_batch ? (
                              <Checkbox
                                checked={selectedFailedJobs.has(job.id)}
                                onCheckedChange={() => toggleFailedJobSelection(job.id)}
                                aria-label={`Select ${job.file_name}`}
                                data-testid={`select-failed-job-${job.id}`}
                              />
                            ) : (
                              <span className="w-4 h-4 block" />
                            )}
                          </TableCell>
                        )}
                        <TableCell>
                          <Badge variant="outline" className={FILE_TYPES[job.file_type]?.color || ""}>
                            {FILE_TYPES[job.file_type]?.name || job.file_type}
                          </Badge>
                        </TableCell>
                        <TableCell className="font-medium">{job.file_name}</TableCell>
                        <TableCell>
                          <div className="space-y-1">
                            {job.status === "processing" ? (
                              <div className="space-y-1">
                                <div className="flex items-center gap-2 text-sm text-yellow-600">
                                  <Loader2 className="w-3 h-3 animate-spin" />
                                  <span>{job.stage || "Processing..."}</span>
                                </div>
                                {job.total_rows > 0 && (
                                  <>
                                    <div className="flex items-center justify-between text-xs text-gray-600">
                                      <span>
                                        {job.records_processed?.toLocaleString() || 0} / {job.total_rows?.toLocaleString()} records
                                      </span>
                                      <span className="font-medium text-yellow-700">
                                        {Math.min(100, Math.round(((job.records_processed || 0) / job.total_rows) * 100))}%
                                      </span>
                                    </div>
                                    <Progress
                                      value={Math.min(100, ((job.records_processed || 0) / job.total_rows) * 100)}
                                      className="h-1.5"
                                      data-testid={`upload-progress-${job.id || job._id || 'pending'}`}
                                    />
                                  </>
                                )}
                              </div>
                            ) : (
                              <>
                                <div className="text-sm">
                                  {/* If records_processed is 0 but total_rows exists, this was likely a failed/incomplete upload */}
                                  {job.records_processed > 0 ? (
                                    <>
                                      <span className="font-medium text-green-600">{job.records_processed?.toLocaleString()}</span>
                                      {job.total_rows > 0 && job.total_rows !== job.records_processed && (
                                        <>
                                          <span className="text-gray-400"> / </span>
                                          <span className="text-gray-600">{job.total_rows?.toLocaleString()}</span>
                                        </>
                                      )}
                                    </>
                                  ) : job.total_rows > 0 ? (
                                    <span className="font-medium text-orange-500">{job.total_rows?.toLocaleString()}</span>
                                  ) : (
                                    <span className="text-gray-400">-</span>
                                  )}
                                  <span className="text-gray-400 text-sm ml-1">records</span>
                                </div>
                                {job.isin_count > 0 && (
                                  <div className="text-sm text-gray-500">
                                    {job.isin_count?.toLocaleString()} ISINs mapped
                                  </div>
                                )}
                                {/* BSE NAV specific info */}
                                {job.file_type === "BSE_NAV" && job.unique_isins > 0 && (
                                  <div className="text-sm text-gray-500">
                                    {job.unique_isins?.toLocaleString()} ISINs, {job.unique_dates || 1} date(s)
                                    {job.date_range?.min && (
                                      <span className="ml-1">
                                        ({job.date_range.min === job.date_range.max 
                                          ? job.date_range.min 
                                          : `${job.date_range.min} to ${job.date_range.max}`})
                                      </span>
                                    )}
                                  </div>
                                )}
                              </>
                            )}
                            {job.file_type === "WBR9" && job.holdings_count > 0 && (
                              <div className="text-sm text-gray-500">
                                {job.investor_count?.toLocaleString() || 0} investors, {job.holdings_count?.toLocaleString() || 0} holdings
                              </div>
                            )}
                            {job.missing_pan_count > 0 && (
                              <div className="text-sm text-orange-500 flex items-center gap-1">
                                <AlertTriangle className="w-3 h-3" />
                                {job.missing_pan_count} missing PAN
                              </div>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          {job.status === "completed" ? (
                            <div className="space-y-1">
                              <span className="flex items-center gap-1 text-green-600">
                                <CheckCircle2 className="w-4 h-4" />
                                Completed
                              </span>
                              {job.stage && job.stage !== "Completed" && (
                                <span className="text-sm text-gray-400">{job.stage}</span>
                              )}
                            </div>
                          ) : job.status === "failed" ? (
                            <div>
                              <span className="flex items-center gap-1 text-red-600">
                                <XCircle className="w-4 h-4" />
                                Failed
                              </span>
                              {job.stage && (
                                <span className="text-sm text-gray-400 block">{job.stage}</span>
                              )}
                              {job.error && (
                                <p className="text-sm text-red-400 mt-1 max-w-[200px] truncate" title={job.error}>
                                  {job.error}
                                </p>
                              )}
                            </div>
                          ) : (
                            <div className="space-y-1">
                              <span className="flex items-center gap-1 text-yellow-600">
                                <Loader2 className="w-4 h-4 animate-spin" />
                                Processing
                              </span>
                              {job.stage && (
                                <span className="text-sm text-blue-500 block">{job.stage}</span>
                              )}
                            </div>
                          )}
                        </TableCell>
                        <TableCell className="text-gray-500">
                          {new Date(job.created_at).toLocaleString()}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-1">
                            {job.is_subbroker_batch && job.error_file_url && (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-teal-700 hover:text-teal-800 hover:bg-teal-50"
                                title="Download error CSV"
                                onClick={async () => {
                                  try {
                                    const resp = await api.get(job.error_file_url, { responseType: "blob" });
                                    const url = window.URL.createObjectURL(new Blob([resp.data]));
                                    const a = document.createElement("a");
                                    a.href = url;
                                    a.download = `subbroker_errors_${job.id.slice(0, 8)}.csv`;
                                    a.click();
                                    window.URL.revokeObjectURL(url);
                                  } catch (e) {
                                    toast.error("Failed to download error CSV");
                                  }
                                }}
                                data-testid={`download-subbroker-errors-${job.id}`}
                              >
                                <Download className="w-4 h-4" />
                              </Button>
                            )}
                            {!job.is_subbroker_batch && (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                onClick={() => handleDeleteUpload(job.id, job.file_type)}
                                disabled={deletingJobId === job.id}
                              >
                                {deletingJobId === job.id ? (
                                  <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                  <Trash2 className="w-4 h-4" />
                                )}
                              </Button>
                            )}
                            {job.is_subbroker_batch && (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                title="Delete this sub broker upload batch (removes all sub brokers created via this file)"
                                onClick={async () => {
                                  if (!window.confirm(
                                    `Delete this sub broker upload batch?\n\nThis will PERMANENTLY remove every sub broker (and their login) that was created via this file. This cannot be undone.`
                                  )) return;
                                  setDeletingJobId(job.id);
                                  try {
                                    const res = await api.delete(`/subbroker/upload-history/${job.id}`);
                                    const d = res.data || {};
                                    toast.success(
                                      `Batch deleted — ${d.sub_brokers_deleted || 0} sub brokers, ${d.users_deleted || 0} user accounts removed`
                                    );
                                    fetchUploadHistory();
                                  } catch (e) {
                                    toast.error("Failed to delete sub broker batch", {
                                      description: e.response?.data?.detail || "An error occurred",
                                    });
                                  } finally {
                                    setDeletingJobId(null);
                                  }
                                }}
                                disabled={deletingJobId === job.id}
                                data-testid={`delete-subbroker-batch-${job.id}`}
                              >
                                {deletingJobId === job.id ? (
                                  <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                  <Trash2 className="w-4 h-4" />
                                )}
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* PROFILE removed as a separate tab — now lives under Upload Files */}
      </Tabs>
        </TabsContent>

        <TabsContent value="account" className="mt-4" data-testid="account-master-content">
          <AccountMasterInner />
        </TabsContent>
      </Tabs>
    </div>
  );
};

function ProfileLink({ to, icon: Icon, label, desc, testid, downloadUrl, downloadFileName, downloadTitle }) {
  const handleDownload = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      const token = localStorage.getItem("token");
      const resp = await fetch(`${process.env.REACT_APP_BACKEND_URL}${downloadUrl}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!resp.ok) {
        toast.error("Download failed");
        return;
      }
      const blob = await resp.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = downloadFileName || "download.xlsx";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error("Download failed");
    }
  };

  return (
    <Link to={to} data-testid={testid} className="block">
      <div className="relative">
        {downloadUrl && (
          <button
            type="button"
            onClick={handleDownload}
            className="absolute top-2 right-2 z-10 p-2 bg-white rounded-full shadow-md hover:bg-gray-50 transition-colors"
            title={downloadTitle || "Download template"}
            data-testid={`${testid}-download-btn`}
          >
            <Download className="w-4 h-4 text-gray-600" />
          </button>
        )}
        <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center cursor-pointer transition-all hover:border-teal-400 hover:bg-gray-50">
          <div className="flex flex-col items-center gap-3">
            <div className="p-3 rounded-full bg-teal-100 text-teal-700">
              <Icon className="w-6 h-6" />
            </div>
            <div>
              <p className="font-medium text-gray-800">{label}</p>
              <p className="text-sm text-gray-500">{desc}</p>
            </div>
            <p className="text-sm text-gray-400">Click to open</p>
            {downloadUrl && (
              <p className="text-xs text-amber-600">Click download icon (↓) to get the template</p>
            )}
          </div>
        </div>
      </div>
    </Link>
  );
}

export default Masters;
